import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import {SnackbarProvider} from 'notistack';

import Page from "components/page/Page";
import DateRangePicker from "components/DateRangePicker";

import OrderSearchBar from "./components/OrderSearchBar";
import FiltersMenu from "./components/Filter/FiltersMenu";
import StatusesTab from "./components/StatusesTab";
import QueryParamsSync from "./components/QueryParamsSync";
import FilterQueryParamsSync from "./components/FilterQueryParamsSync";
import SearchQueryParamsSync from "./components/SearchQueryParamsSync";
import FilterOptionsSync from "./components/FilterOptionsSync";
import FilterRestoreSync from "./components/FilterRestoreSync";
import ExportOrdersButton from "./components/ExportOrdersButton";
import ExportLineItemsButton from "./components/ExportLineItemsButton";
import ItemsPerPageSelect from "components/select/itemsPerPageSelect";
import MobileView from "./components/MobileView";

import {OrderListContext} from "./context/OrderListContext";
import {DateRangePickerContextProvider} from "contexts/DateRangePickerContext";
import OrderTable from "./components/OrderTable";

import {ITEM_PER_PAGE} from "config/_pagination";
import {OMSRoutes} from "utils/OMSRoutes";

import useIsMobile from "hooks/useIsMobile";

import {getInitialQueryParams, getInitialDates, withDefaultDatesIfNeeded} from "./utils/getInitialListState";
import {isPeriodNeutralized} from "./utils/isPeriodNeutralized";

OrderList.propTypes = {
    match: PropTypes.object
};

function OrderList(props) {
    const isMobile = useIsMobile();
    const history = useHistory();

    const [initialQueryParams] = useState(() => getInitialQueryParams(props));
    const [{restoredStartDate, restoredEndDate}] = useState(() => getInitialDates(initialQueryParams));

    const [search, setSearch] = useState(() => new URLSearchParams(initialQueryParams).get('search') ?? '');
    const [filterMenuSelected, setFilterMenuSelected] = useState('LineItemStatus');
    const [filters, setFilters] = useState({});
    const [organizationCouponCodes, setOrganizationCouponCodes] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState([]);

    const [queryParams, setQueryParams] = useState(() => withDefaultDatesIfNeeded(initialQueryParams, restoredStartDate, restoredEndDate));

    // keeps the URL in sync with the live list state (filters, search, period,
    // sort, page, ...) so the browser's back button returns to it instead of
    // the stale URL the list was first opened with
    useEffect(() => {
        history.replace(`${OMSRoutes.ORDERS}/${encodeURIComponent(queryParams)}`);
    }, [queryParams, history]);

    function handleItemsPerPageChange(event) {
        setQueryParams((previousQueryParams) => {
            const params = new URLSearchParams(previousQueryParams);

            params.set('itemsPerPage', event.target.value);
            params.set('page', '1');

            return params.toString();
        });
    }

    return (
        <SnackbarProvider>
            <DateRangePickerContextProvider restoredStartDate={restoredStartDate} restoredEndDate={restoredEndDate}>
                <OrderListContext.Provider value={{
                    search,
                    setSearch,
                    filterMenuSelected,
                    setFilterMenuSelected,
                    filters,
                    setFilters,
                    organizationCouponCodes,
                    setOrganizationCouponCodes,
                    selectedFilters,
                    setSelectedFilters,
                    queryParams,
                    setQueryParams,
                    isMobile
                }}>
                    <QueryParamsSync />
                    <FilterQueryParamsSync />
                    <SearchQueryParamsSync />
                    <FilterOptionsSync />
                    <FilterRestoreSync />

                    {!isMobile && (
                        <Page
                            titleId='oms.order.list.title'
                            fullPage
                            border
                            // same side/bottom padding as the default CardHeader (0.9375rem/1.875rem),
                            // only the top gap is bumped to 24px per the maquette
                            paddingHeader="24px 1.875rem 0.9375rem"
                            rightContent={(
                                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <OrderSearchBar search={search} setSearch={setSearch} />
                                    <DateRangePicker displayFormat={'DD/MM/YYYY'} compact suspended={!!search || isPeriodNeutralized(new URLSearchParams(queryParams))} />
                                </div>
                            )}
                        >
                            <FiltersMenu />
                            <StatusesTab />
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    <ExportOrdersButton />
                                    <ExportLineItemsButton />
                                </div>

                                <ItemsPerPageSelect
                                    defaultItemsPerPage={new URLSearchParams(queryParams).get('itemsPerPage') ?? ITEM_PER_PAGE}
                                    onChange={handleItemsPerPageChange}
                                />
                            </div>
                            <OrderTable />
                        </Page>
                    )}

                    {isMobile && (
                        <MobileView search={search} setSearch={setSearch} />
                    )}
                </OrderListContext.Provider>
            </DateRangePickerContextProvider>
        </SnackbarProvider>
    );
}

export default OrderList;
